import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { fetchCrops } from '../../features/crops/cropSlice';
import { fetchTasks } from '../../features/tasks/taskSlice';
import { fetchResources } from '../../features/resources/resourceSlice';
import { fetchSales } from '../../features/sales/salesSlice';
import { fetchWeatherLocations } from '../../features/weather/weatherSlice';
import Card from '../ui/Card';
import WeatherWidget from '../weather/WeatherWidget';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import TaskStatusSummary from './stats/TaskStatusSummary';
import MonthlyGrowth from './stats/MonthlyGrowth';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatters';
import { Layers, Calendar, Package, DollarSign, Activity } from 'react-feather';

const DashboardOverview: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Fetch all required data
  useEffect(() => {
    dispatch(fetchCrops());
    dispatch(fetchTasks());
    dispatch(fetchResources());
    dispatch(fetchSales());
    dispatch(fetchWeatherLocations());
  }, [dispatch]);

  // Get data and loading states from Redux
  const { crops, loading: cropsLoading, error: cropsError } = useSelector((state: RootState) => state.crops);
  const { tasks, loading: tasksLoading, error: tasksError } = useSelector((state: RootState) => state.tasks);
  const { resources, loading: resourcesLoading, error: resourcesError } = useSelector((state: RootState) => state.resources);
  const { sales, loading: salesLoading, error: salesError } = useSelector((state: RootState) => state.sales);

  // Check if any data is loading
  const isLoading = cropsLoading || tasksLoading || resourcesLoading || salesLoading;

  // Combine all errors
  const errors = [cropsError, tasksError, resourcesError, salesError].filter(Boolean);

  // If loading, show loading spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If there are errors, show them
  if (errors.length > 0) {
    return (
      <div className="space-y-4">
        {errors.map((error, index) => (
          <ErrorMessage key={index} message={error || 'An error occurred'} />
        ))}
      </div>
    );
  }

  // Calculate statistics
  const activeCrops = crops.filter(crop => crop.status === 'active');
  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const lowStockResources = resources.filter(
    resource => resource.minimum_quantity && resource.quantity <= resource.minimum_quantity
  );

  const totalSales = sales.reduce((sum, sale) => sum + sale.total_amount, 0);
  const totalProfit = sales.reduce((sum, sale) => sum + (sale.profit || 0), 0);

  // Calculate monthly growth
  const currentMonth = new Date().getMonth();
  const currentMonthSales = sales
    .filter(sale => new Date(sale.sale_date).getMonth() === currentMonth)
    .reduce((sum, sale) => sum + sale.total_amount, 0);
  
  const lastMonthSales = sales
    .filter(sale => new Date(sale.sale_date).getMonth() === currentMonth - 1)
    .reduce((sum, sale) => sum + sale.total_amount, 0);

  const monthlyGrowth = lastMonthSales ? 
    ((currentMonthSales - lastMonthSales) / lastMonthSales) * 100 : 0;

  const stats = [
    {
      title: t('dashboard.stats.activeCrops'),
      value: activeCrops.length,
      icon: Layers,
      color: 'text-green-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
      details: `${crops.length} ${t('dashboard.stats.totalCrops')}`,
      trend: activeCrops.length > 0 ? '+' + activeCrops.length : '0'
    },
    {
      title: t('dashboard.stats.pendingTasks'),
      value: pendingTasks.length,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      details: `${tasks.length} ${t('dashboard.stats.totalTasks')}`,
      trend: pendingTasks.length > 0 ? 
        `${pendingTasks.length} ${pendingTasks.length === 1 ? t('tasks.status.pending') : t('dashboard.stats.pendingTasks')}` : 
        t('dashboard.stats.noPendingTasks')
    },
    {
      title: t('dashboard.stats.totalResources'),
      value: resources.length,
      icon: Package,
      color: 'text-purple-600',
      bgColor: 'bg-gradient-to-br from-purple-50 to-pink-50',
      details: `${lowStockResources.length} ${t('dashboard.stats.lowStock')}`,
      trend: lowStockResources.length > 0 ? 
        `${lowStockResources.length} ${t('dashboard.stats.lowStock')}` : 
        t('dashboard.stats.stockGood')
    },
    {
      title: t('dashboard.stats.totalSales'),
      value: formatCurrency(totalSales),
      icon: DollarSign,
      color: totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600',
      bgColor: totalProfit >= 0 ? 'bg-gradient-to-br from-emerald-50 to-green-50' : 'bg-gradient-to-br from-red-50 to-pink-50',
      details: `${t('dashboard.stats.profit')}: ${formatCurrency(totalProfit)}`,
      trend: `${monthlyGrowth >= 0 ? '+' : ''}${monthlyGrowth.toFixed(1)}% ${t('dashboard.stats.vsLastMonth')}`
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.overview')}</h1>
          <p className="text-gray-600 mt-2">{t('dashboard.welcomeBack')}</p>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <Activity className="w-5 h-5 text-green-500" />
          <span className="text-gray-600">
            {t('dashboard.lastUpdated')}: {new Date().toLocaleTimeString()}
          </span>
        </div>
      </div>
      
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card 
            key={index} 
            className={`${stat.bgColor} border-none transition-all duration-300 hover:shadow-xl hover:-translate-y-2 cursor-pointer`}
          >
            <div className="flex items-start justify-between p-6">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-2">{stat.title}</p>
                <p className={`text-3xl font-bold ${stat.color} mb-2`}>
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500 mb-3">{stat.details}</p>
                <p className={`text-xs ${stat.color} font-medium bg-white bg-opacity-50 px-2 py-1 rounded-full inline-block`}>
                  {stat.trend}
                </p>
              </div>
              <div className={`${stat.color} p-3 rounded-xl bg-white bg-opacity-20`}>
                <stat.icon className="w-8 h-8" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Weather Widget */}
        <div className="lg:col-span-2">
          <WeatherWidget />
        </div>

        {/* Task Summary and Monthly Growth */}
        <div className="space-y-6">
          <TaskStatusSummary tasks={tasks} />
          <MonthlyGrowth growth={monthlyGrowth} />
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;